import { useAccount, useReadContract, useBalance } from 'wagmi';
import { parseEther, formatEther } from 'viem';
import BigNumber from 'bignumber.js';
import { formatDistance } from 'date-fns';
import { CONTRACT_ADDRESSES, NETWORK_VARS } from 'src/constants';
import StakingABI from './abi/Staking';
import { ApiWithdrawalRequest } from 'src/types';

export function useAssetsToSharesRatio() {
  const { data, isLoading, error } = useReadContract({
    abi: StakingABI,
    address: CONTRACT_ADDRESSES.staking,
    functionName: 'convertToShares',
    args: [parseEther('1')],
  });

  const assetsToSharesRatio = BigNumber(String(1))
    .times(String(data || 1e18))
    .div(1e18)
    .toFixed();
  const sharesToAssetsRatio = BigNumber(1).div(assetsToSharesRatio).toFixed();

  return {
    assetsToSharesRatio,
    sharesToAssetsRatio,
    isLoadingRatio: isLoading,
    ratioError: error,
  };
}

export function useSharesBalance() {
  const { address } = useAccount();

  const {
    data = BigInt(0),
    isLoading,
    error,
  } = useReadContract({
    abi: StakingABI,
    address: CONTRACT_ADDRESSES.staking,
    functionName: 'balanceOf',
    args: [address || '0x'],
    query: {
      enabled: !!address,
    },
  });

  return {
    sharesBalance: data,
    sharesBalanceFormatted: BigNumber(data).div(1e18).toFixed(),
    isLoadingSharesBalance: isLoading,
    sharesBalanceError: error,
  };
}

export function useStakingIsPaused() {
  const {
    data = false,
    isLoading,
    error,
  } = useReadContract({
    abi: StakingABI,
    address: CONTRACT_ADDRESSES.staking,
    functionName: 'paused',
  });

  return {
    stakingIsPaused: data,
    stakingIsPausedMessage: 'Contract is temporarily paused',
    isLoadingStakingIsPaused: isLoading,
    stakingIsPausedError: error,
  };
}

export function useTokenBalance({
  tokenAddress,
  enabled,
}: {
  tokenAddress?: string;
  enabled?: boolean;
} = {}) {
  const { address } = useAccount();

  const {
    data: balance,
    isLoading,
    error,
  } = useBalance({
    address,
    token:
      tokenAddress === NETWORK_VARS.tokensAddresses.MON
        ? undefined
        : (tokenAddress as `0x${string}`),
    query: {
      enabled: !!address && enabled,
    },
  });

  return {
    balance,
    isLoadingBalance: isLoading,
    balanceError: error,
  };
}

export function useUserRequestData({
  perPage = 1000,
  page = 1,
}: {
  perPage?: number;
  page?: number;
}) {
  const { address } = useAccount();

  const {
    data: requestDataCount = 0n,
    isLoading: isLoadingRequestDataCount,
    error: errorRequestDataCount,
  } = useReadContract({
    abi: StakingABI,
    address: CONTRACT_ADDRESSES.staking,
    functionName: 'getUserRequestDataCount',
    args: [address || '0x'],
    query: {
      enabled: !!address,
    },
  });

  const {
    data: requestData = [],
    isLoading,
    error,
  } = useReadContract({
    abi: StakingABI,
    address: CONTRACT_ADDRESSES.staking,
    functionName: 'getUserRequestData',
    args: [
      address || '0x',
      BigInt(Math.max(0, Number(requestDataCount) - perPage * page)),
      BigInt(perPage),
    ],
    query: {
      enabled: !!address && !isLoadingRequestDataCount && !!perPage,
    },
  });

  const transformedRequests: ApiWithdrawalRequest[] = requestData
    .toReversed()
    .map((request: any) => ({
      id: request.id.toString(),
      claimed: request.claimed,
      is_claimable: request.claimable,
      shares: request.shares.toString(),
      assets: request.assets.toString(),
      requested_at: Number(request.timestamp),
      unlock_epoch: Number(request.unlockEpoch),
    }));

  return {
    walletRequests: transformedRequests,
    isLoadingWalletRequests: isLoading,
    walletRequestsError: error,
  };
}

export function useWithdrawalDelay() {
  const {
    data = 1,
    isLoading,
    error,
  } = useReadContract({
    abi: StakingABI,
    address: CONTRACT_ADDRESSES.staking,
    functionName: 'withdrawalDelay',
  });

  return {
    withdrawalDelay: Number(data),
    isLoadingWithdrawalDelay: isLoading,
    withdrawalDelayError: error,
  };
}
